import { createContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import {
  signIn,
  signOut,
  fetchAuthSession,
  getCurrentUser,
  confirmSignIn,
  type SignInInput
} from 'aws-amplify/auth';

export interface User {
  email: string;
  userId: string;
  tenantId: string;
  role: string;
  firstName?: string;
  lastName?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (input: SignInInput) => Promise<{ nextStep: string }>;
  handleNewPassword: (newPassword: string) => Promise<void>;
  logout: () => Promise<void>;
  getToken: () => Promise<string | null>;
  checkAuth: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      setIsLoading(true);
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;

      const email =
        idToken?.payload?.email?.toString() ?? currentUser.signInDetails?.loginId ?? '';
      const userId = currentUser.userId;
      
      // Use 'custom:tenant_id' to match SAM template and backend
      const tenantId =
        idToken?.payload?.['custom:tenant_id']?.toString() ?? '';
      const role = 
        idToken?.payload?.['custom:role']?.toString() ?? '';

      const firstName = idToken?.payload?.given_name?.toString() ?? '';
      const lastName = idToken?.payload?.family_name?.toString() ?? '';

      setUser({ email, userId, tenantId, role, firstName, lastName });
      setIsAuthenticated(true);
    } catch (err) {
      console.error('Auth check failed:', err);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const login = useCallback(
    async (input: SignInInput) => {
      setIsLoading(true);
      try {
        try { await signOut(); } catch (e) {} // Clear ghost sessions
        const result = await signIn(input);
        
        if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
           return { nextStep: result.nextStep.signInStep };
        }

        if (result.isSignedIn) {
          await checkAuth();
          return { nextStep: 'DONE' };
        }
        
        return { nextStep: result.nextStep.signInStep };
      } finally {
        setIsLoading(false);
      }
    },
    [checkAuth],
  );

  const handleNewPassword = useCallback(async (newPassword: string) => {
    setIsLoading(true);
    try {
      const result = await confirmSignIn({ challengeResponse: newPassword });
      if (result.isSignedIn) {
        await checkAuth();
      }
    } finally {
      setIsLoading(false);
    }
  }, [checkAuth]);

  const logout = useCallback(async () => {
    await signOut();
    localStorage.removeItem('activeTenantId');
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  const getToken = useCallback(async (): Promise<string | null> => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() ?? null;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        login,
        handleNewPassword,
        logout,
        getToken,
        checkAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
